import { calculators, constants, enums, formatters } from "@fraction/shared";

/** The fields of a broker account that the tier computation reads. */
export interface BrokerTierAccount {
  brokerType?: enums.BrokerType;
  numberOfDealsLastYear?: number;
}

export interface BrokerTier {
  title: string;
  tier: enums.BrokerType;
  next?: enums.BrokerType;
  dealsRequiredToNextTier?: number;
  bps: number;
  multiplier: number;
}

export const BROKER_TIERS: Record<enums.BrokerType, BrokerTier> = {
  [enums.BrokerType.STANDARD]: {
    tier: enums.BrokerType.STANDARD,
    title: "Standard",
    next: enums.BrokerType.BRONZE,
    dealsRequiredToNextTier: constants.FUNDED_DEALS_FOR_BRONZE_BROKER,
    bps: 0,
    multiplier: 0,
  },
  [enums.BrokerType.BRONZE]: {
    tier: enums.BrokerType.BRONZE,
    title: "Bronze",
    next: enums.BrokerType.SILVER,
    dealsRequiredToNextTier: constants.FUNDED_DEALS_FOR_SILVER_BROKER,
    bps: constants.BPS_FOR_BRONZE_BROKER,
    multiplier: 1,
  },
  [enums.BrokerType.SILVER]: {
    tier: enums.BrokerType.SILVER,
    title: "Silver",
    next: enums.BrokerType.GOLD,
    dealsRequiredToNextTier: constants.FUNDED_DEALS_FOR_GOLD_BROKER,
    bps: constants.BPS_FOR_SILVER_BROKER,
    multiplier: 2,
  },
  [enums.BrokerType.GOLD]: {
    tier: enums.BrokerType.GOLD,
    title: "Gold",
    bps: constants.BPS_FOR_GOLD_BROKER,
    multiplier: 4,
  },
};

export interface BrokerTierResult {
  brokerType: enums.BrokerType;
  currentTier: BrokerTier;
  nextTier: BrokerTier | undefined;
  numberOfDealsToNextTier: number;
  /** Percentage string (e.g. "40%") for a progress bar width. */
  progressToNextTier: string;
  calculatePoints: (cents: number) => number | undefined;
}

/**
 * Pure broker-tier computation over a broker account, shared by web + mobile so
 * the tier ladder + points math live in one place. Each platform's
 * `useBrokerTier` wraps its own `useBrokerAccount` and spreads this result.
 */
export function computeBrokerTier(brokerAccount?: BrokerTierAccount): BrokerTierResult {
  const brokerType: enums.BrokerType = brokerAccount?.brokerType || enums.BrokerType.STANDARD;
  const currentTier = BROKER_TIERS[brokerType];

  const numberOfDealsToNextTier = Math.max(
    currentTier?.dealsRequiredToNextTier
      ? currentTier.dealsRequiredToNextTier - (brokerAccount?.numberOfDealsLastYear || 0)
      : 0,
    0
  );

  const progressToNextTier = formatters.number.getPercentageFromDecimal(
    (brokerAccount?.numberOfDealsLastYear || 0) /
      (currentTier?.dealsRequiredToNextTier || brokerAccount?.numberOfDealsLastYear || 1)
  );

  const calculatePoints = (cents: number) =>
    currentTier?.bps !== undefined
      ? Math.floor(
          (calculators.points.bpsToPercent(currentTier.bps) * formatters.number.getDollars(cents)) /
            constants.DOLLARS_PER_REWARDS_POINT
        )
      : undefined;

  return {
    brokerType,
    currentTier,
    nextTier: currentTier.next ? BROKER_TIERS[currentTier.next] : undefined,
    numberOfDealsToNextTier,
    progressToNextTier,
    calculatePoints,
  };
}
